import React, { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import inventoryService from '../../services/inventoryService';
import labelService from '../../services/labelService';
import { getPreferredPrinterConfig } from '../../utils/printerPreferences';
import { useStore } from '../../context/StoreContext';
import { PRICING_OPTION_OPTIONS } from '../../config/productOptions';

const PSP_CLASSIFICATION_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'Permanent_Liquidation', label: 'Permanent Liquidation' },
  { value: 'Temporary_Liquidation', label: 'Temporary Liquidation' },
  { value: 'Bulk_Stock_Purchase', label: 'Bulk Stock Purchase' },
  { value: 'Bulk&Normal_Stock_Purchase', label: 'Bulk & Normal Stock Purchase' },
  { value: 'Normal_Purchase', label: 'Normal Purchase' },
];

const STOCK_ADJUSTMENT_REASON_OPTIONS = [
  {
    value: 'handheld_update',
    label: 'Handheld stock update',
    prefix: 'Handheld stock update',
    hint: 'General manual stock correction.',
  },
  {
    value: 'stocktake_count',
    label: 'Stocktake count',
    prefix: 'Stocktake count',
    hint: 'Use during stocktake so historical snapshots can be rebuilt cleanly.',
  },
  {
    value: 'damaged_stock',
    label: 'Damaged stock',
    prefix: 'Damaged stock',
    hint: 'Use when removing damaged or unsellable units.',
  },
  {
    value: 'receiving_correction',
    label: 'Receiving correction',
    prefix: 'Receiving correction',
    hint: 'Use when correcting a receiving discrepancy.',
  },
  {
    value: 'other',
    label: 'Other',
    prefix: '',
    hint: 'Enter a fully custom reason.',
  },
];

const formatCurrency = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric.toFixed(2) : '0.00';
};

const resolveDisplayCost = (product) => product?.storeCost ?? product?.costPrice ?? 0;

const getStockAdjustmentReasonOption = (value) =>
  STOCK_ADJUSTMENT_REASON_OPTIONS.find((option) => option.value === value) || STOCK_ADJUSTMENT_REASON_OPTIONS[0];

const buildStockAdjustmentReason = ({ reasonType, details }) => {
  const option = getStockAdjustmentReasonOption(reasonType);
  const trimmedDetails = String(details || '').trim();

  if (option.value === 'other') {
    return trimmedDetails;
  }

  if (!trimmedDetails) {
    return option.prefix;
  }

  return `${option.prefix}: ${trimmedDetails}`;
};

const HandheldLookupPage = () => {
  const { activeStore } = useStore();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [adjustQty, setAdjustQty] = useState('');
  const [productNameInput, setProductNameInput] = useState('');
  const [skuInput, setSkuInput] = useState('');
  const [barcodeInput, setBarcodeInput] = useState('');
  const [pspClassificationInput, setPspClassificationInput] = useState('');
  const [pricingOptionInput, setPricingOptionInput] = useState('');
  const [priceInput, setPriceInput] = useState('0.00');
  const [priceSuccess, setPriceSuccess] = useState('');
  const [adjustReasonType, setAdjustReasonType] = useState('handheld_update');
  const [adjustReasonDetails, setAdjustReasonDetails] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const selectedReasonOption = getStockAdjustmentReasonOption(adjustReasonType);
  const resolvedAdjustReason = buildStockAdjustmentReason({
    reasonType: adjustReasonType,
    details: adjustReasonDetails,
  });

  const runSearch = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    if (!activeStore?._id) {
      setError('Select a store to search inventory.');
      return;
    }
    if (!query.trim()) {
      setError('Enter a product name, SKU, or barcode.');
      return;
    }
    setLoading(true);
    try {
      const data = await inventoryService.searchProducts(activeStore._id, query.trim());
      setResults(data);
      if (data.length === 0) {
        setSelectedProduct(null);
        setError('No products found.');
      }
    } catch (err) {
      console.error(err);
      setError('Unable to fetch products.');
    } finally {
      setLoading(false);
    }
  };

  const getPrinterPlugin = () => {
    if (!Capacitor.isPluginAvailable('ReceiptPrinter')) {
      return null;
    }
    return window?.Capacitor?.Plugins?.ReceiptPrinter || null;
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setAdjustQty(product.stock ?? 0);
    setProductNameInput(product.name || '');
    setSkuInput(product.sku || '');
    setBarcodeInput(product.barcode || '');
    setPspClassificationInput(product.pspClassification || '');
    setPricingOptionInput(product.pricing_option || '');
    setPriceInput(Number(product.price || 0).toFixed(2));
    setSuccess('');
    setPriceSuccess('');
    setError('');
    setIsCreating(false);
  };

  const handleAdjustStock = async () => {
    if (!activeStore?._id || !selectedProduct) return;
    const newQty = Number(adjustQty);
    if (Number.isNaN(newQty)) {
      setError('Enter a valid quantity.');
      return;
    }
    if (!resolvedAdjustReason) {
      setError('Select a reason or enter a custom reason.');
      return;
    }
    try {
      await inventoryService.adjustStock(selectedProduct._id, {
        storeId: activeStore._id,
        newQuantity: newQty,
        reason: resolvedAdjustReason,
      });
      setSelectedProduct({ ...selectedProduct, stock: newQty });
      setSuccess('Stock updated successfully.');
      setError('');
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to adjust stock.');
    }
  };

  const handleAddProduct = () => {
    setSelectedProduct(null);
    setAdjustQty('');
    setProductNameInput('');
    setSkuInput('');
    setBarcodeInput('');
    setPspClassificationInput('');
    setPricingOptionInput('');
    setPriceInput('0.00');
    setPriceSuccess('');
    setSuccess('');
    setError('');
    setIsCreating(true);
  };

  const handleSaveProduct = async () => {
    const payload = {
      name: productNameInput.trim(),
      sku: skuInput.trim(),
      barcode: barcodeInput.trim(),
      pspClassification: pspClassificationInput,
      pricing_option: pricingOptionInput,
      price: Number(priceInput) || 0,
      storeId: activeStore?._id,
    };
    if (!selectedProduct?._id) {
      payload.costPrice = 0;
    }
    if (!payload.name) {
      setError('Product name is required.');
      return;
    }
    try {
      let saved;
      if (selectedProduct?._id) {
        const updatedProduct = await inventoryService.updateProduct(selectedProduct._id, payload);
        saved = { ...selectedProduct, ...updatedProduct };
      } else {
        saved = await inventoryService.createProduct(payload);
      }
      setSelectedProduct(saved);
      setPriceSuccess(selectedProduct?._id ? 'Product updated successfully.' : 'Product created successfully.');
      setError('');
      setSuccess('');
      setIsCreating(false);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to save product.');
    }
  };

  const handleQuickPrint = async () => {
    const product = selectedProduct;
    if (!product?._id) {
      setError('Select or save a product first.');
      return;
    }
    try {
      const response = await labelService.renderLabel({
        templateId: 'sample-label',
        productId: product._id,
        data: {
          name: productNameInput || product.name,
          price: Number(priceInput) || 0,
          unit: 'ea',
          barcodeNumber: barcodeInput || product.barcode || product.sku,
        },
      });
      const plugin = getPrinterPlugin();
      if (!plugin) {
        setError('Printer plugin unavailable.');
        return;
      }
      const config = getPreferredPrinterConfig('label');
      const payload = { html: response.html, jobType: 'label' };
      if (config.transport === 'network' && config.host) {
        payload.networkHost = config.host;
        payload.networkPort = Number(config.port) || 9100;
      } else if (config.bluetoothName) {
        payload.bluetoothNameHint = config.bluetoothName;
      }
      await plugin.print(payload);
      setSuccess('Label sent to printer.');
      setError('');
    } catch (err) {
      console.error('[HandheldLookup] Quick print failed', err);
      setError(err.response?.data?.message || 'Unable to print label.');
    }
  };

  return (
    <div className="p-4 space-y-4">
      <form onSubmit={runSearch} className="bg-white rounded-2xl shadow p-4 space-y-2">
        <label className="text-xs uppercase tracking-[0.3em] text-slate-400">Product search</label>
        <div className="flex gap-2">
          <input
            type="text"
            className="flex-1 border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-amber-400"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Scan or type SKU / barcode"
          />
          <button
            type="submit"
            className="px-4 py-2 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800"
          >
            {loading ? 'Searching…' : 'Search'}
          </button>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        {success && <p className="text-sm text-green-600">{success}</p>}
        {priceSuccess && <p className="text-sm text-green-600">{priceSuccess}</p>}
        <button
          type="button"
          onClick={handleAddProduct}
          className="text-xs text-amber-600 font-semibold underline"
        >
          {isCreating ? 'Reset new product form' : 'Create new product'}
        </button>
      </form>

      {results.length > 0 && (
        <div className="bg-white rounded-2xl shadow divide-y">
          {results.slice(0, 8).map((product) => (
            <button
              key={product._id}
              onClick={() => handleSelectProduct(product)}
              className="w-full text-left p-4 hover:bg-slate-50"
            >
              <p className="text-sm font-semibold">{product.name}</p>
              <p className="text-xs text-slate-500">
                SKU: {product.sku} • Barcode: {product.barcode || 'n/a'}
              </p>
              <p className="text-xs text-slate-500">Stock: {product.stock ?? '0'}</p>
            </button>
          ))}
        </div>
      )}

      {(selectedProduct || isCreating) && (
        <div className="bg-white rounded-2xl shadow p-4 space-y-4">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-slate-400">Product details</p>
            <h3 className="text-xl font-semibold">{productNameInput || selectedProduct?.name || 'New product'}</h3>
            {selectedProduct?._id && (
              <p className="text-sm text-slate-500">Current stock: {selectedProduct.stock ?? 0}</p>
            )}
            <p className="text-sm text-slate-500">
              Price: ${Number(priceInput || selectedProduct?.price || 0).toFixed(2)}
            </p>
            <p className="text-sm text-slate-500">
              Cost: ${formatCurrency(resolveDisplayCost(selectedProduct))}
            </p>
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Name</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-xl px-3 py-2"
              value={productNameInput}
              onChange={(e) => setProductNameInput(e.target.value)}
            />
            <label className="block text-sm font-medium text-slate-700 mt-3">SKU</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-xl px-3 py-2"
              value={skuInput}
              onChange={(e) => setSkuInput(e.target.value)}
            />
            <label htmlFor="mpos-product-barcode" className="block text-sm font-medium text-slate-700 mt-3">Barcode</label>
            <input
              id="mpos-product-barcode"
              type="text"
              className="w-full border border-gray-300 rounded-xl px-3 py-2"
              value={barcodeInput}
              onChange={(e) => setBarcodeInput(e.target.value)}
            />
            <label htmlFor="mpos-product-psp-classification" className="block text-sm font-medium text-slate-700 mt-3">PSP classification</label>
            <select
              id="mpos-product-psp-classification"
              className="w-full border border-gray-300 rounded-xl px-3 py-2 bg-white"
              value={pspClassificationInput}
              onChange={(e) => setPspClassificationInput(e.target.value)}
            >
              {PSP_CLASSIFICATION_OPTIONS.map((option) => (
                <option key={option.value || 'empty'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <label htmlFor="mpos-product-pricing-option" className="block text-sm font-medium text-slate-700 mt-3">Pricing option</label>
            <select
              id="mpos-product-pricing-option"
              className="w-full border border-gray-300 rounded-xl px-3 py-2 bg-white"
              value={pricingOptionInput}
              onChange={(e) => setPricingOptionInput(e.target.value)}
            >
              {PRICING_OPTION_OPTIONS.map((option) => (
                <option key={option.value || 'empty'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <label htmlFor="mpos-product-cost" className="block text-sm font-medium text-slate-700 mt-3">Cost</label>
            <input
              id="mpos-product-cost"
              type="text"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 bg-slate-100 text-slate-600"
              value={`$${formatCurrency(resolveDisplayCost(selectedProduct))}`}
              readOnly
              aria-label="Read-only cost"
            />
            <label className="block text-sm font-medium text-slate-700">New quantity</label>
            <input
              type="number"
              className="w-full border border-gray-300 rounded-xl px-3 py-2"
              value={adjustQty}
              onChange={(e) => setAdjustQty(e.target.value)}
            />
            <label htmlFor="mpos-stock-reason-type" className="block text-sm font-medium text-slate-700 mt-3">Reason preset</label>
            <select
              id="mpos-stock-reason-type"
              className="w-full border border-gray-300 rounded-xl px-3 py-2 bg-white"
              value={adjustReasonType}
              onChange={(e) => setAdjustReasonType(e.target.value)}
            >
              {STOCK_ADJUSTMENT_REASON_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-500">{selectedReasonOption.hint}</p>
            <label htmlFor="mpos-stock-reason-details" className="block text-sm font-medium text-slate-700 mt-3">
              {adjustReasonType === 'other' ? 'Custom reason' : 'Reason details'}
            </label>
            <input
              id="mpos-stock-reason-details"
              type="text"
              className="w-full border border-gray-300 rounded-xl px-3 py-2"
              value={adjustReasonDetails}
              onChange={(e) => setAdjustReasonDetails(e.target.value)}
              placeholder={
                adjustReasonType === 'stocktake_count'
                  ? 'Optional: aisle, bay, or count session'
                  : adjustReasonType === 'other'
                    ? 'Enter the full reason'
                    : 'Optional extra detail'
              }
            />
            <p className="text-xs text-slate-500">
              Saved reason: <span className="font-medium text-slate-700">{resolvedAdjustReason || 'Enter a custom reason'}</span>
            </p>
            <label htmlFor="mpos-product-price" className="block text-sm font-medium text-slate-700 mt-3">Price</label>
            <input
              id="mpos-product-price"
              type="number"
              step="0.01"
              className="w-full border border-gray-300 rounded-xl px-3 py-2"
              value={priceInput}
              onChange={(e) => setPriceInput(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleAdjustStock}
              disabled={!selectedProduct?._id}
              className="px-4 py-2 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-600 disabled:opacity-50"
            >
              Update stock
            </button>
            <button
              type="button"
              onClick={handleSaveProduct}
              className="px-4 py-2 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800"
            >
              {selectedProduct?._id ? 'Update product' : 'Save product'}
            </button>
            <button
              type="button"
              onClick={handleQuickPrint}
              disabled={!selectedProduct?._id}
              className="px-4 py-2 rounded-xl border border-slate-300 text-slate-700 disabled:opacity-50"
            >
              Print label
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HandheldLookupPage;
